/* Which days are good (or forbidden) to fast — from the Hijri date (Umm
   al-Qura via Intl, adjustable ±2 days for local moon sighting) and the
   weekday. SHARED by the app and the push worker (fast reminders), so both
   agree. Keep it free of DOM/Worker APIs. */
import { addDays, hijriOn } from './prayer';

export type FastRuleId = 'ramadan' | 'monThu' | 'white' | 'ashura' | 'arafah' | 'dhulHijjah' | 'shawwal' | 'muharram' | 'shaban';

export interface FastRule {
  id: FastRuleId;
  label: string;
  ar: string;
  /** obligatory (Ramadan), strong sunnah, or simply recommended */
  strength: 'fard' | 'sunnah' | 'recommended';
  note: string;
}

export const FAST_RULES: Record<FastRuleId, FastRule> = {
  ramadan: { id: 'ramadan', label: 'Ramadan', ar: 'رمضان', strength: 'fard', note: 'Obligatory fast. Missed days must be made up (qaḍāʾ).' },
  arafah: { id: 'arafah', label: 'Day of ʿArafah', ar: 'يوم عرفة', strength: 'sunnah', note: 'Expiates the sins of the previous year and the coming year (Muslim).' },
  ashura: { id: 'ashura', label: 'ʿĀshūrāʾ', ar: 'عاشوراء', strength: 'sunnah', note: '10 Muḥarram expiates the previous year (Muslim); fast the 9th with it.' },
  white: { id: 'white', label: 'White days', ar: 'الأيام البيض', strength: 'sunnah', note: 'The 13th, 14th and 15th of each Hijri month — like fasting the whole month (al-Bukhārī).' },
  monThu: { id: 'monThu', label: 'Monday & Thursday', ar: 'الاثنين والخميس', strength: 'sunnah', note: 'Deeds are presented on Mondays and Thursdays (at-Tirmidhī).' },
  shawwal: { id: 'shawwal', label: 'Six of Shawwāl', ar: 'ست من شوال', strength: 'sunnah', note: 'With Ramadan, like fasting the whole year (Muslim). Any 6 days after Eid.' },
  dhulHijjah: { id: 'dhulHijjah', label: 'First days of Dhul-Ḥijjah', ar: 'عشر ذي الحجة', strength: 'recommended', note: 'The best days of the year for good deeds (al-Bukhārī).' },
  muharram: { id: 'muharram', label: 'Month of Muḥarram', ar: 'شهر الله المحرم', strength: 'recommended', note: 'The best fasting after Ramadan is in Allah’s month, Muḥarram (Muslim).' },
  shaban: { id: 'shaban', label: 'Shaʿbān', ar: 'شعبان', strength: 'recommended', note: 'The Prophet ﷺ fasted much of Shaʿbān (al-Bukhārī, Muslim).' },
};

export interface FastDayInfo {
  dayKey: string;
  hijri: ReturnType<typeof hijriOn>;
  ramadan: boolean;
  rules: FastRule[];
  /** fasting is not permitted (Eid / Tashrīq) */
  forbidden: string | null;
}

const dow = (dayKey: string) => new Date(`${dayKey}T12:00:00Z`).getUTCDay();

export function fastingInfo(dayKey: string, hijriOffset = 0): FastDayInfo {
  const h = hijriOn(dayKey, hijriOffset);
  const out: FastDayInfo = { dayKey, hijri: h, ramadan: false, rules: [], forbidden: null };
  if (!h) return out;
  if (h.m === 10 && h.d === 1) { out.forbidden = 'Eid al-Fiṭr'; return out; }
  if (h.m === 12 && h.d === 10) { out.forbidden = 'Eid al-Aḍḥā'; return out; }
  if (h.m === 12 && h.d >= 11 && h.d <= 13) { out.forbidden = 'Days of Tashrīq'; return out; }
  if (h.m === 9) { out.ramadan = true; out.rules.push(FAST_RULES.ramadan); return out; }
  const r = out.rules;
  if (h.m === 12 && h.d === 9) r.push(FAST_RULES.arafah);
  if (h.m === 1 && (h.d === 9 || h.d === 10)) r.push(FAST_RULES.ashura);
  if (h.d >= 13 && h.d <= 15) r.push(FAST_RULES.white);
  if (h.m === 10) r.push(FAST_RULES.shawwal);
  if (h.m === 12 && h.d <= 8) r.push(FAST_RULES.dhulHijjah);
  const wd = dow(dayKey);
  if (wd === 1 || wd === 4) r.push(FAST_RULES.monThu);
  if (h.m === 1 && h.d !== 9 && h.d !== 10) r.push(FAST_RULES.muharram);
  if (h.m === 8) r.push(FAST_RULES.shaban);
  return out;
}

/** The best reason to fast this day, if any (for one-line labels). */
export const headlineRule = (i: FastDayInfo) => i.rules[0] ?? null;

/** Next `count` days (from `from`, inclusive) that carry a sunnah/obligatory fast. */
export function upcomingFasts(from: string, days = 30, hijriOffset = 0): FastDayInfo[] {
  const out: FastDayInfo[] = [];
  for (let i = 0; i < days; i++) {
    const info = fastingInfo(addDays(from, i), hijriOffset);
    if (info.rules.some((r) => r.strength !== 'recommended')) out.push(info);
  }
  return out;
}

/** Gregorian days of Ramadan for a given Hijri year (scans around the expected date). */
export function ramadanDays(hijriYear: number, near: string, hijriOffset = 0): string[] {
  const out: string[] = [];
  // Ramadan moves ~11 days a year; scanning ±400 days around `near` is plenty.
  for (let i = -400; i <= 400; i++) {
    const k = addDays(near, i);
    const h = hijriOn(k, hijriOffset);
    if (h && h.y === hijriYear && h.m === 9) out.push(k);
    else if (out.length) break;
  }
  return out;
}
